import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Tabs,
  Tab,
  Typography,
  Grid,
  Card,
  CardContent,
  LinearProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line
} from 'recharts';
import { format, getYear, startOfYear, endOfYear, eachWeekOfInterval, startOfWeek, endOfWeek } from 'date-fns';
import { fr } from 'date-fns/locale';
import PageTitle from '../components/layout/PageTitle';
import { useTechnicians } from '../contexts/TechniciansContext';
import { useSkills } from '../contexts/SkillsContext';
import { useOnCalls } from '../contexts/OnCallsContext';
import { getHolidaysForYear, hasHolidayInPeriod } from '../services/holidaysService';
import useVisibilityPermissions from '../hooks/useVisibilityPermissions';

const StatisticsPage = () => {
  const [currentTab, setCurrentTab] = useState(0);
  const [selectedYear, setSelectedYear] = useState(getYear(new Date()));
  const [loading, setLoading] = useState(true);
  const { canViewTab } = useVisibilityPermissions();
  
  // Utilisation des contextes API
  const { technicians, loading: techniciansLoading } = useTechnicians();
  const { skills, loading: skillsLoading } = useSkills();
  const { onCalls, loading: onCallsLoading } = useOnCalls();
  
  const [data, setData] = useState({
    onCalls: [],
    technicians: {},
    skills: {},
    holidays: [],
    statistics: {
      coverage: {},
      technicianStats: {},
      skillDistribution: {},
      weeklyDistribution: {},
      workload: {}
    }
  });

  // Définir les onglets disponibles avec leurs clés
  const tabs = [
    { key: 'coverage', label: 'Couverture', render: () => renderCoverageTab() },
    { key: 'technicians', label: 'Techniciens', render: () => renderTechniciansTab() },
    { key: 'analysis', label: 'Analyses', render: () => renderAnalysisTab() }
  ];

  // Filtrer les onglets selon les permissions
  const visibleTabs = tabs.filter(tab => canViewTab('statistics', tab.key));

  // Ajuster l'onglet actuel si nécessaire
  useEffect(() => {
    if (visibleTabs.length > 0 && currentTab >= visibleTabs.length) {
      setCurrentTab(0);
    }
  }, [visibleTabs.length, currentTab]);

  useEffect(() => {
    loadStatistics();
  }, [selectedYear, technicians, skills, onCalls]);

  // Générer la liste des années disponibles (N-2 à N+2)
  const getAvailableYears = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let year = currentYear - 2; year <= currentYear + 2; year++) {
      years.push(year);
    }
    return years;
  };

  const loadStatistics = async () => {
    // Attendre que les contextes aient chargé leurs données
    if (techniciansLoading || skillsLoading || onCallsLoading || 
        technicians.length === 0 || skills.length === 0) {
      return;
    }
    
    setLoading(true);
    try {
      const holidays = await getHolidaysForYear(selectedYear);

      // Convertir les données des contextes en maps pour un accès facile
      const techniciansMap = technicians.reduce((acc, tech) => {
        acc[tech.id] = tech;
        return acc;
      }, {});

      const skillsMap = skills.reduce((acc, skill) => {
        acc[skill.id] = skill;
        return acc;
      }, {});

      // Filtrer les astreintes de l'année sélectionnée
      const yearOnCalls = onCalls.filter(onCall => {
        const year = getYear(new Date(onCall.startDate));
        return year === selectedYear;
      });

      // Calculer les statistiques
      const statistics = calculateStatistics(yearOnCalls, techniciansMap, skillsMap, holidays, selectedYear);

      setData({
        onCalls: yearOnCalls,
        technicians: techniciansMap,
        skills: skillsMap,
        holidays,
        statistics
      });
    } catch (error) {
      console.error('Erreur lors du chargement des statistiques:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateStatistics = (onCalls, technicians, skills, holidays, year) => {
    // 1. Statistiques de couverture par compétence
    const coverage = {};
    const totalWeeks = 52;

    Object.values(skills).forEach(skill => {
      const skillOnCalls = onCalls.filter(onCall => onCall.skills && onCall.skills.includes(skill.id));
      const coveredWeeks = new Set();
      
      skillOnCalls.forEach(onCall => {
        const startDate = new Date(onCall.startDate);
        const endDate = new Date(onCall.endDate);
        
        // Calculer les semaines couvertes plus précisément
        const yearStart = new Date(year, 0, 1); // 1er janvier de l'année
        const startWeek = Math.floor((startDate - yearStart) / (7 * 24 * 60 * 60 * 1000)) + 1;
        const endWeek = Math.floor((endDate - yearStart) / (7 * 24 * 60 * 60 * 1000)) + 1;
        
        for (let week = Math.max(1, startWeek); week <= Math.min(52, endWeek); week++) {
          coveredWeeks.add(week);
        }
      });

      coverage[skill.id] = {
        skill,
        coveredWeeks: coveredWeeks.size,
        totalWeeks,
        percentage: Math.round((coveredWeeks.size / totalWeeks) * 100),
        assignments: skillOnCalls.length
      };
    });

    // 2. Statistiques par technicien
    const technicianStats = {};
    Object.values(technicians).forEach(tech => {
      const techOnCalls = onCalls.filter(onCall => onCall.technicianId === tech.id);
      const totalDays = techOnCalls.reduce((sum, onCall) => {
        const start = new Date(onCall.startDate);
        const end = new Date(onCall.endDate);
        return sum + Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
      }, 0);

      // Calculer les primes
      let totalPrimes = 0;
      let holidayAssignments = 0;
      techOnCalls.forEach(onCall => {
        const hasHoliday = hasHolidayInPeriod(onCall.startDate, onCall.endDate, holidays);
        if (hasHoliday) {
          totalPrimes += 600; // Prime avec jour férié
          holidayAssignments++;
        } else {
          totalPrimes += 478; // Prime normale
        }
      });

      const skillsCount = {};
      const allSkills = new Set();
      techOnCalls.forEach(onCall => {
        if (onCall.skills) {
          onCall.skills.forEach(skillId => {
            skillsCount[skillId] = (skillsCount[skillId] || 0) + 1;
            allSkills.add(skillId);
          });
        }
      });

      technicianStats[tech.id] = {
        technician: tech,
        totalAssignments: techOnCalls.length,
        totalDays,
        holidayAssignments,
        totalPrimes,
        averageDaysPerAssignment: techOnCalls.length > 0 ? Math.round(totalDays / techOnCalls.length) : 0,
        skillsDistribution: skillsCount,
        allSkills: tech.skills || [],
        mostUsedSkill: Object.keys(skillsCount).reduce((a, b) => 
          skillsCount[a] > skillsCount[b] ? a : b, Object.keys(skillsCount)[0])
      };
    });

    // 3. Distribution des compétences
    const skillDistribution = {};
    onCalls.forEach(onCall => {
      onCall.skills.forEach(skillId => {
        if (!skillDistribution[skillId]) {
          skillDistribution[skillId] = {
            skill: skills[skillId],
            count: 0,
            totalDays: 0
          };
        }
        skillDistribution[skillId].count++;
        const start = new Date(onCall.startDate);
        const end = new Date(onCall.endDate);
        skillDistribution[skillId].totalDays += Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
      });
    });

    // 4. Distribution hebdomadaire
    const weeklyDistribution = {};
    for (let week = 1; week <= 52; week++) {
      weeklyDistribution[week] = {
        week,
        assignments: 0,
        technicians: new Set(),
        skills: new Set()
      };
    }

    onCalls.forEach(onCall => {
      const startDate = new Date(onCall.startDate);
      const endDate = new Date(onCall.endDate);
      const weeks = eachWeekOfInterval({ start: startDate, end: endDate });
      
      weeks.forEach(week => {
        const weekNumber = Math.ceil((week - startOfYear(new Date(year))) / (7 * 24 * 60 * 60 * 1000));
        if (weekNumber >= 1 && weekNumber <= 52 && weeklyDistribution[weekNumber]) {
          weeklyDistribution[weekNumber].assignments++;
          weeklyDistribution[weekNumber].technicians.add(onCall.technicianId);
          onCall.skills.forEach(skillId => {
            weeklyDistribution[weekNumber].skills.add(skillId);
          });
        }
      });
    });

    // 5. Charge de travail et statistiques des techniciens
    const workload = {
      totalAssignments: onCalls.length,
      totalDays: onCalls.reduce((sum, onCall) => {
        const start = new Date(onCall.startDate);
        const end = new Date(onCall.endDate);
        return sum + Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
      }, 0),
      averageAssignmentDuration: 0,
      busiestMonth: null,
      quietestMonth: null
    };

    if (onCalls.length > 0) {
      workload.averageAssignmentDuration = Math.round(workload.totalDays / onCalls.length);
    }

    // Calculer les statistiques min/max/moyenne des astreintes par technicien
    const technicianAssignmentCounts = Object.values(technicianStats).map(stat => stat.totalAssignments);
    const technicianMetrics = {
      minAssignments: technicianAssignmentCounts.length > 0 ? Math.min(...technicianAssignmentCounts) : 0,
      maxAssignments: technicianAssignmentCounts.length > 0 ? Math.max(...technicianAssignmentCounts) : 0,
      averageAssignments: technicianAssignmentCounts.length > 0 ? 
        Math.round(technicianAssignmentCounts.reduce((sum, count) => sum + count, 0) / technicianAssignmentCounts.length) : 0
    };

    return {
      coverage,
      technicianStats,
      skillDistribution,
      weeklyDistribution,
      workload,
      technicianMetrics
    };
  };

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const handleYearChange = (event) => {
    setSelectedYear(event.target.value);
  };

  const renderCoverageTab = () => {
    const coverageData = Object.values(data.statistics.coverage)
      .sort((a, b) => a.skill.name.localeCompare(b.skill.name));
    const chartData = coverageData.map(item => ({
      name: item.skill.name,
      percentage: item.percentage,
      assignments: item.assignments,
      color: item.skill.color
    }));

    return (
      <Grid container spacing={3}>
        {/* Résumé global */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Résumé de la couverture {selectedYear}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="h4" color="primary">
                    {Object.keys(data.skills).length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Compétences totales
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="h4" color="success.main">
                    {coverageData.filter(c => c.percentage >= 90).length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Bien couvertes (≥90%)
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="h4" color="warning.main">
                    {coverageData.filter(c => c.percentage >= 50 && c.percentage < 90).length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Moyennement couvertes
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="h4" color="error.main">
                    {coverageData.filter(c => c.percentage < 50).length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Mal couvertes (&lt;50%)
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Graphique de couverture */}
        <Grid item xs={12} lg={8}>
          <Card>
            <CardContent sx={{ pb: 2 }}>
              <Typography variant="h6" gutterBottom>
                Pourcentage de couverture par compétence
              </Typography>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip 
                    formatter={(value, name) => [value + '%', name === 'percentage' ? 'Couverture' : name]}
                  />
                  <Bar dataKey="percentage" fill="#8884d8" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Détail par compétence */}
        <Grid item xs={12} lg={4}>
          <Card>
            <CardContent sx={{ pb: 2 }}>
              <Typography variant="h6" gutterBottom>
                Détail par compétence
              </Typography>
              <Box sx={{ maxHeight: 280, overflow: 'auto' }}>
                {coverageData.map((item) => (
                  <Box key={item.skill.id} sx={{ mb: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          backgroundColor: item.skill.color,
                          borderRadius: '50%',
                          mr: 1
                        }}
                      />
                      <Typography variant="body2" sx={{ flexGrow: 1, fontSize: '0.85rem' }}>
                        {item.skill.name}
                      </Typography>
                      <Chip
                        label={`${item.percentage}%`}
                        size="small"
                        color={
                          item.percentage >= 90 ? 'success' :
                          item.percentage >= 50 ? 'warning' : 'error'
                        }
                        sx={{ height: '20px', fontSize: '0.7rem' }}
                      />
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={item.percentage}
                      sx={{
                        height: 6,
                        borderRadius: 3,
                        backgroundColor: 'grey.200',
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: item.skill.color
                        }
                      }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                      {item.coveredWeeks}/52 semaines
                    </Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  const renderTechniciansTab = () => {
    const techStats = Object.values(data.statistics.technicianStats);
    const chartData = techStats
      .sort((a, b) => a.technician.lastName.localeCompare(b.technician.lastName))
      .map(stat => ({
      name: stat.technician.lastName, // Seulement le nom de famille pour condenser
      assignments: stat.totalAssignments,
      primes: stat.totalPrimes
    }));

    return (
      <Grid container spacing={3}>
        {/* Résumé global */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Statistiques des techniciens {selectedYear}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={2.4}>
                  <Typography variant="h4" color="primary">
                    {Object.keys(data.technicians).length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Techniciens actifs
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={2.4}>
                  <Typography variant="h4" color="success.main">
                    {data.statistics.workload.totalAssignments}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Astreintes totales
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={2.4}>
                  <Typography variant="h4" color="info.main">
                    {data.statistics.technicianMetrics?.minAssignments || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Nb min d'astreintes
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={2.4}>
                  <Typography variant="h4" color="warning.main">
                    {data.statistics.technicianMetrics?.averageAssignments || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Nb moyen d'astreintes
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={2.4}>
                  <Typography variant="h4" color="error.main">
                    {data.statistics.technicianMetrics?.maxAssignments || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Nb max d'astreintes
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Graphique des astreintes par technicien */}
        <Grid item xs={12} lg={8}>
          <Card>
            <CardContent sx={{ pb: 2 }}>
              <Typography variant="h6" gutterBottom>
                Nombre d'astreintes par technicien
              </Typography>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="name" 
                    angle={-45}
                    textAnchor="end"
                    height={80}
                    fontSize={12}
                  />
                  <YAxis />
                  <Tooltip 
                    formatter={(value, name) => [value, name === 'assignments' ? 'Astreintes' : name]}
                  />
                  <Bar dataKey="assignments" fill="#82ca9d" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Tableau détaillé */}
        <Grid item xs={12} lg={4}>
          <Card>
            <CardContent sx={{ pb: 2 }}>
              <Typography variant="h6" gutterBottom>
                Détail par technicien
              </Typography>
              <TableContainer sx={{ maxHeight: 280, overflow: 'auto' }}>
                <Table size="small" stickyHeader sx={{ tableLayout: 'fixed', width: '100%' }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontSize: '0.75rem', width: '45%', px: 1 }}>Technicien</TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.75rem', width: '18%', px: 0.5 }}>Astr.</TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.75rem', width: '18%', px: 0.5 }}>J.F.</TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.75rem', width: '19%', px: 0.5 }}>Primes</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {techStats
                      .sort((a, b) => a.technician.lastName.localeCompare(b.technician.lastName))
                      .map((stat) => (
                        <TableRow key={stat.technician.id}>
                          <TableCell sx={{ py: 0.5, px: 1, overflow: 'hidden' }}>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                fontSize: '0.75rem', 
                                fontWeight: 500,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                lineHeight: 1.2
                              }}
                              title={`${stat.technician.firstName} ${stat.technician.lastName}`}
                            >
                              {stat.technician.firstName} {stat.technician.lastName}
                            </Typography>
                            <Box sx={{ 
                              display: 'flex', 
                              flexWrap: 'wrap', 
                              gap: 0.25, 
                              mt: 0.25,
                              maxHeight: '32px',
                              overflow: 'hidden'
                            }}>
                              {stat.allSkills
                                .map(skillId => data.skills[skillId])
                                .filter(Boolean)
                                .sort((a, b) => a.name.localeCompare(b.name))
                                .slice(0, 3)
                                .map(skill => (
                                  <Chip
                                    key={skill.id}
                                    label={skill.name}
                                    size="small"
                                    sx={{
                                      backgroundColor: `${skill.color}20`,
                                      border: `1px solid ${skill.color}40`,
                                      fontSize: '0.55rem',
                                      height: '14px',
                                      minWidth: 'auto',
                                      '& .MuiChip-label': { 
                                        px: 0.25,
                                        lineHeight: 1
                                      }
                                    }}
                                  />
                                ))}
                              {stat.allSkills.length > 3 && (
                                <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'text.secondary' }}>
                                  +{stat.allSkills.length - 3}
                                </Typography>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell align="center" sx={{ fontSize: '0.75rem', px: 0.5 }}>
                            {stat.totalAssignments}
                          </TableCell>
                          <TableCell align="center" sx={{ fontSize: '0.75rem', px: 0.5 }}>
                            {stat.holidayAssignments}
                          </TableCell>
                          <TableCell align="center" sx={{ fontSize: '0.7rem', fontWeight: 500, px: 0.5 }}>
                            {stat.totalPrimes.toLocaleString()}€
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  const renderAnalysisTab = () => {
    const skillDistData = Object.values(data.statistics.skillDistribution)
      .sort((a, b) => (a.skill?.name || 'Inconnu').localeCompare(b.skill?.name || 'Inconnu'))
      .map(item => ({
      name: item.skill?.name || 'Inconnu',
      value: item.count,
      color: item.skill?.color || '#cccccc'
    }));

    // Données pour l'équilibre de charge entre techniciens
    const technicianWorkloadData = Object.values(data.statistics.technicianStats)
      .sort((a, b) => a.technician.lastName.localeCompare(b.technician.lastName))
      .map(stat => ({
        name: `${stat.technician.firstName} ${stat.technician.lastName}`,
        shortName: stat.technician.lastName,
        assignments: stat.totalAssignments,
        averageAssignments: data.statistics.technicianMetrics?.averageAssignments || 0
      }));

    return (
      <Grid container spacing={3}>
        {/* Distribution des compétences */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent sx={{ pb: 2 }}>
              <Typography variant="h6" gutterBottom>
                Répartition des astreintes par compétence
              </Typography>
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie
                    data={skillDistData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={70}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {skillDistData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Équilibre de charge entre techniciens */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent sx={{ pb: 2 }}>
              <Typography variant="h6" gutterBottom>
                Équilibre de charge entre techniciens
              </Typography>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={technicianWorkloadData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="shortName" 
                    angle={-45}
                    textAnchor="end"
                    height={60}
                    fontSize={11}
                  />
                  <YAxis />
                  <Tooltip 
                    formatter={(value, name) => [value, name === 'assignments' ? 'Astreintes' : 'Moyenne']}
                    labelFormatter={(label) => {
                      const tech = technicianWorkloadData.find(t => t.shortName === label);
                      return tech ? tech.name : label;
                    }}
                  />
                  <Bar dataKey="assignments" fill="#82ca9d" name="Astreintes" />
                  <Bar dataKey="averageAssignments" fill="#ffc658" name="Moyenne" opacity={0.7} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Métriques avancées */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Métriques avancées
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="h4" color="primary">
                    {Math.round(data.statistics.workload.totalDays / 365 * 100)}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Couverture annuelle
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="h4" color="success.main">
                    {Object.values(data.statistics.technicianStats).length > 0 ? 
                      Math.round(data.statistics.workload.totalAssignments / Object.values(data.statistics.technicianStats).length) : 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Astreintes/technicien
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="h4" color="info.main">
                    {Object.keys(data.statistics.skillDistribution).length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Compétences utilisées
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="h4" color="warning.main">
                    {data.statistics.technicianMetrics ? 
                      (data.statistics.technicianMetrics.maxAssignments - data.statistics.technicianMetrics.minAssignments) : 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Écart max de charge
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  if (loading) {
    return (
      <Box>
        <PageTitle title="Statistiques" />
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Typography>Chargement des statistiques...</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <PageTitle
        title="Statistiques"
        actions={
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Année</InputLabel>
            <Select
              value={selectedYear}
              label="Année"
              onChange={handleYearChange}
            >
              {getAvailableYears().map(year => (
                <MenuItem key={year} value={year}>{year}</MenuItem>
              ))}
            </Select>
          </FormControl>
        }
      />

      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          {visibleTabs.map(tab => (
            <Tab key={tab.key} label={tab.label} />
          ))}
        </Tabs>
      </Paper>

      <Box sx={{ mt: 2 }}>
        {visibleTabs[currentTab]?.render()}
      </Box>
    </Box>
  );
};

export default StatisticsPage; 